import { createContext, useCallback, useContext, useRef, useState } from 'react';
import FileOpenDialog from '@/components/FileOpenDialog';

/**
 * FileDialogContext — app-level host for the unified File Open modal.
 *
 * The modal can't live next to each caller because it needs a single mount
 * point and a stable pending-promise resolver. Instead, we provide a
 * `requestFileOpen()` imperative API at the context layer. Callers await the
 * promise it returns and get back whatever the user picked, shaped like the
 * legacy native picker result plus an optional `cloudDocId`.
 *
 * The hook at useFileDialog.js decides — per platform + per cloud state —
 * whether to call this or fall back to the native OS picker.
 */
const FileDialogContext = createContext(null);

export function useFileDialogContext() {
  return useContext(FileDialogContext);
}

export function FileDialogProvider({ children }) {
  const [state, setState] = useState({ open: false, accept: '.pdf', multiple: false, initialTab: null });
  const pendingRef = useRef(null);

  const requestFileOpen = useCallback((accept = '.pdf', { multiple = false, initialTab = null } = {}) => {
    // Cancel any prior pending request — only one modal at a time.
    if (pendingRef.current) {
      try { pendingRef.current.resolve(null); } catch { /* ignore */ }
    }
    return new Promise((resolve) => {
      pendingRef.current = { resolve };
      setState({ open: true, accept, multiple, initialTab });
    });
  }, []);

  const close = useCallback((result) => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setState((s) => ({ ...s, open: false }));
    if (pending) pending.resolve(result);
  }, []);

  return (
    <FileDialogContext.Provider value={{ requestFileOpen }}>
      {children}
      <FileOpenDialog
        open={state.open}
        accept={state.accept}
        multiple={state.multiple}
        initialTab={state.initialTab}
        onPicked={(picked) => close(picked)}
        onCancel={() => close(null)}
      />
    </FileDialogContext.Provider>
  );
}
